import React, { useState, useEffect, useMemo } from 'react';

/**
 * Extracts coordinate pair (lat,lng) or place title from any full Google Maps URL
 * @param {string} url - Google Maps URL
 * @returns {string} Extracted query or coordinates
 */
export const extractQueryFromGoogleMapsUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();

    try {
        // 1. Check for /maps/search/QueryOrCoords in path (e.g. /maps/search/28.588670,+77.189416)
        const searchPathMatch = trimmed.match(/\/maps\/search\/([^/?&]+)/);
        if (searchPathMatch && searchPathMatch[1]) {
            return decodeURIComponent(searchPathMatch[1].replace(/\+/g, ' '));
        }

        // 2. Check for @lat,lng format (e.g. /@28.6139,77.2090,17z/)
        const atCoordsMatch = trimmed.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
        if (atCoordsMatch) {
            return `${atCoordsMatch[1]},${atCoordsMatch[2]}`;
        }

        // 3. Check for !3dlat!4dlng embedded coordinates format
        const dataCoordsMatch = trimmed.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
        if (dataCoordsMatch) {
            return `${dataCoordsMatch[1]},${dataCoordsMatch[2]}`;
        }

        // 4. Check for ?q= or &query= params
        if (trimmed.includes('?') || trimmed.includes('&')) {
            const urlObj = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
            const qParam = urlObj.searchParams.get('query') || urlObj.searchParams.get('q');
            if (qParam && qParam.trim()) {
                return qParam.trim();
            }
        }

        // 5. Check for /place/Place+Name in path
        if (trimmed.includes('/place/')) {
            const placeMatch = trimmed.match(/\/place\/([^/@?]+)/);
            if (placeMatch && placeMatch[1]) {
                return decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
            }
        }

        // 6. Check for raw coordinate pair in URL string
        const coordMatch = trimmed.match(/(-?\d+\.\d{3,})\s*,\s*(-?\d+\.\d{3,})/);
        if (coordMatch) {
            return `${coordMatch[1]},${coordMatch[2]}`;
        }
    } catch (e) {
        console.warn('Error extracting query from Google Maps URL:', e);
    }

    return '';
};

/**
 * Checks if a Google Maps link is a shortened shareable URL
 * @param {string} url - Link string
 * @returns {boolean}
 */
export const isShortGoogleMapsUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const lower = url.toLowerCase();
    return lower.includes('maps.app.goo.gl') || lower.includes('goo.gl/maps');
};

/**
 * Synchronous resolver for Google Maps target (prioritizes googleMapsLink over venue)
 * @param {Object} event - Event object with location, googleMapsLink, eventMode
 * @returns {Object|null}
 */
export const resolveEventMapTarget = (event) => {
    if (!event) return null;

    const eventMode = event.eventMode || event.mode || 'offline';
    if (eventMode === 'online') {
        return null;
    }

    const mapsLink = typeof event.googleMapsLink === 'string' ? event.googleMapsLink.trim() : '';
    const venue = typeof event.location === 'string' ? event.location.trim() : '';

    // 1. Priority 1: Google Maps Link provided
    if (mapsLink) {
        const extractedQuery = extractQueryFromGoogleMapsUrl(mapsLink);
        const directUrl = mapsLink.startsWith('http') ? mapsLink : `https://${mapsLink}`;

        // If direct coordinates or place name found in URL
        if (extractedQuery) {
            return {
                source: 'google_maps_link_extracted',
                query: extractedQuery,
                embedUrl: `https://maps.google.com/maps?q=${encodeURIComponent(extractedQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`,
                directUrl,
                displayLabel: venue || extractedQuery,
                isShortUrl: false,
            };
        }

        // If it is a shortened link (maps.app.goo.gl)
        const isShort = isShortGoogleMapsUrl(mapsLink);
        if (isShort) {
            return {
                source: 'google_maps_short_link',
                query: venue || '',
                embedUrl: venue && venue.toLowerCase() !== 'online'
                    ? `https://maps.google.com/maps?q=${encodeURIComponent(venue)}&t=&z=15&ie=UTF8&iwloc=&output=embed`
                    : `https://maps.google.com/maps?q=${encodeURIComponent(mapsLink)}&t=&z=15&ie=UTF8&iwloc=&output=embed`,
                directUrl,
                displayLabel: venue || 'View on Google Maps',
                isShortUrl: true,
            };
        }

        // Default direct link fallback
        return {
            source: 'google_maps_link_direct',
            query: venue || '',
            embedUrl: venue && venue.toLowerCase() !== 'online'
                ? `https://maps.google.com/maps?q=${encodeURIComponent(venue)}&t=&z=15&ie=UTF8&iwloc=&output=embed`
                : `https://maps.google.com/maps?q=${encodeURIComponent(mapsLink)}&t=&z=15&ie=UTF8&iwloc=&output=embed`,
            directUrl,
            displayLabel: venue || 'View on Google Maps',
            isShortUrl: false,
        };
    }

    // 2. Priority 2: Fallback to Venue string if googleMapsLink is empty
    if (venue && venue.toLowerCase() !== 'online') {
        return {
            source: 'venue_string',
            query: venue,
            embedUrl: `https://maps.google.com/maps?q=${encodeURIComponent(venue)}&t=&z=15&ie=UTF8&iwloc=&output=embed`,
            directUrl: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`,
            displayLabel: venue,
            isShortUrl: false,
        };
    }

    return null;
};

/**
 * Reusable Production-Ready Event Map Card Component
 */
export const EventMapSection = ({ event, height = 240, style = {} }) => {
    const initialTarget = useMemo(() => resolveEventMapTarget(event), [event]);
    const [mapTarget, setMapTarget] = useState(initialTarget);

    useEffect(() => {
        setMapTarget(resolveEventMapTarget(event));
    }, [event]);

    if (!mapTarget || (!mapTarget.embedUrl && !mapTarget.directUrl)) {
        return null;
    }

    return (
        <div
            className="event-map-container"
            style={{
                position: 'relative',
                borderRadius: 10,
                overflow: 'hidden',
                border: '1px solid #E2E8F0',
                background: '#F8FAFC',
                ...style,
            }}
        >
            {/* Top Action Pill Button */}
            {mapTarget.directUrl && (
                <a
                    href={mapTarget.directUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        zIndex: 5,
                        background: '#38BDF8',
                        color: '#FFFFFF',
                        padding: '5px 12px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        textDecoration: 'none',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        lineHeight: 1.2,
                    }}
                >
                    Open in Maps ↗
                </a>
            )}

            {/* Embed Map Iframe */}
            {mapTarget.embedUrl ? (
                <iframe
                    title="Event Location Map"
                    width="100%"
                    height={height}
                    frameBorder="0"
                    scrolling="no"
                    marginHeight="0"
                    marginWidth="0"
                    src={mapTarget.embedUrl}
                    style={{ border: 0, display: 'block' }}
                />
            ) : (
                <div style={{ padding: 20, textAlign: 'center', color: '#64748B', fontSize: 13 }}>
                    Location details available via Google Maps link.
                </div>
            )}
        </div>
    );
};

export default resolveEventMapTarget;
